import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ShieldCheck, Star, Heart, Truck, Award, Wheat, ChevronRight, Lock } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import USFarmMap from "@/components/USFarmMap";
import { ProductCard } from "@/pages/Marketplace";
import { useAuth } from "@/context/AuthContext";
import { useLaunchState } from "@/components/Countdown";

// Protein taxonomy — the platform currently offers only Beef and Chicken.
const BEEF_KW = ["cattle", "beef", "bison", "steer"];
const CHICKEN_KW = ["poultry", "chicken", "turkey", "duck"];
const matchKw = (r, kws) =>
  (r.animals_raised || []).some((a) => kws.some((kw) => String(a).toLowerCase().includes(kw)));

export function Stars({ avg, count, size = 12 }) {
  if (!count) return <span className="text-xs text-[var(--ff-text-muted)]">No reviews yet</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={size} className={i <= Math.round(avg) ? "fill-amber-400 text-amber-400" : "text-stone-300"}/>)}
      <span className="ml-1 font-medium">{avg}</span><span className="text-[var(--ff-text-muted)]">({count})</span>
    </span>
  );
}

export function RancherCard({ r }) {
  const launch = useLaunchState();
  const locked = launch && launch.phase !== "launched";
  if (locked) {
    return (
      <div className="card-flat overflow-hidden flex flex-col relative cursor-default" data-testid={`rancher-card-locked-${r.id}`}>
        <div className="h-40 overflow-hidden relative flex items-center justify-center bg-[var(--ff-primary)]">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.25) 0, transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.18) 0, transparent 45%)" }}/>
          <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/15 border border-white/25 text-white">
            <Lock size={22}/>
          </span>
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white text-[var(--ff-primary)] text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"><Lock size={9}/> Revealed at launch</span>
        </div>
        <div className="p-5 flex-1 flex flex-col">
          <p className="text-[11px] uppercase tracking-wider text-[var(--ff-text-muted)] flex items-center gap-1"><Lock size={10}/> Identity hidden</p>
          <h3 className="font-display text-2xl mt-1 leading-tight text-[var(--ff-text-secondary)]">A founding rancher</h3>
          <p className="text-sm text-[var(--ff-text-secondary)] mt-2">We're locking in our founding ranchers now. Names, photos, and storefronts unlock November 27.</p>
          <div className="mt-auto pt-4 flex items-center justify-between text-xs text-[var(--ff-text-muted)]">
            <span className="inline-flex items-center gap-1"><Lock size={11}/> Opens Nov 27</span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <Link to={`/ranchers/${r.id}`} className="card-flat overflow-hidden group flex flex-col" data-testid={`rancher-card-${r.id}`}>
      <div className="h-40 overflow-hidden bg-[var(--ff-muted)] relative">
        {r.image_url && <img src={r.image_url} alt={r.farm_name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>}
        {r.logo_url && <img src={r.logo_url} alt="" className="absolute -bottom-5 left-4 w-14 h-14 rounded-full border-2 border-white bg-white object-cover"/>}
      </div>
      <div className={`p-5 ${r.logo_url ? "pt-7" : ""} flex-1 flex flex-col`}>
        <p className="text-[11px] uppercase tracking-wider text-[var(--ff-text-muted)] flex items-center gap-1"><MapPin size={10}/> {r.location}</p>
        <h3 className="font-display text-2xl mt-1 leading-tight">{r.farm_name}</h3>
        <p className="text-sm text-[var(--ff-text-secondary)] mt-2 line-clamp-2">{r.story}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">{(r.practices || []).slice(0, 3).map(p => <span key={p} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--ff-accent-kraft)]/40 text-[var(--ff-primary)]">{p}</span>)}</div>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs">
          <span>{r.product_count} products{r.drop_count ? ` · ${r.drop_count} drops` : ""}</span>
          <Stars avg={r.rating?.avg} count={r.rating?.count}/>
        </div>
      </div>
    </Link>
  );
}

export function Ranchers() {
  const [ranchers, setRanchers] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const launch = useLaunchState();
  const locked = launch && launch.phase !== "launched";
  useEffect(() => { api.get("/marketplace/ranchers", { params: { q: q || undefined } }).then(r => setRanchers(r.data)).catch(() => {}); }, [q]);
  // Only surface ranchers that raise beef or chicken; hide any seafood/pork/lamb-only partners.
  const shown = useMemo(() => {
    const base = ranchers.filter((r) => {
      const animals = r.animals_raised || [];
      if (!animals.length) return true;
      return matchKw(r, BEEF_KW) || matchKw(r, CHICKEN_KW);
    });
    if (category === "beef") return base.filter((r) => matchKw(r, BEEF_KW));
    if (category === "chicken") return base.filter((r) => matchKw(r, CHICKEN_KW));
    return base;
  }, [ranchers, category]);
  return (
    <div data-testid="ranchers-page" style={{ backgroundImage: "linear-gradient(rgba(250,247,241,0.9), rgba(250,247,241,0.9)), url('/assets/cattle-field.jpg')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <div className="max-w-7xl mx-auto px-6 py-16">
      <Seo title="Meet our ranchers" description="Independent American ranchers and farmers selling premium beef and chicken directly through The Farm To You marketplace." path="/ranchers"/>
      <p className="overline">Meet our ranchers</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">Real ranches. Real people.<br/>Shipped straight to you.</h1>
      <p className="mt-4 max-w-2xl text-[var(--ff-text-secondary)]">Every rancher on The Farm To You is independently owned and must meet our marketplace standards for sourcing, animal welfare, and food safety. Each one runs their own storefront and ships their own orders.</p>
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <input className="input-base max-w-sm" placeholder="Search ranches or states…" value={q} onChange={e => setQ(e.target.value)} data-testid="ranchers-search"/>
        {["", "beef", "chicken"].map(c => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`ranchers-cat-${c || "all"}`}
            className={`px-4 py-1.5 rounded-full text-xs border capitalize ${category === c ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>{c || "All"}</button>
        ))}
      </div>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ranchers-grid">
        {shown.map(r => <RancherCard key={r.id} r={r}/>)}
        {shown.length === 0 && <p className="text-[var(--ff-text-muted)] col-span-full py-10 text-center">No ranchers match yet.</p>}
      </div>
      {!locked && shown.length > 0 && (
        <div className="mt-16" data-testid="farms-map-section">
          <p className="overline text-[var(--ff-primary)]">On the map</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 leading-tight">See where your next dinner comes from.</h2>
          <div className="mt-6"><USFarmMap farms={shown}/></div>
        </div>
      )}
      <div className="mt-16 card-flat p-8 md:p-12 text-white flex flex-col md:flex-row items-center gap-6 justify-between" style={{ background: "var(--ff-primary)" }} data-testid="ranchers-sell-cta">
        <div>
          <p className="overline text-[var(--ff-accent-kraft)]">Are you a rancher?</p>
          <h3 className="font-display text-3xl mt-1">Sell to a nationwide audience of members.</h3>
          <p className="mt-2 text-white/80 max-w-xl">You keep your brand, set your prices, and ship your own product. We bring the customers, the checkout, and the membership.</p>
        </div>
        <Link to="/sell" className="btn-citrus whitespace-nowrap">Become a Selling Partner <ChevronRight size={16}/></Link>
      </div>
      </div>
    </div>
  );
}

export function RancherStorefront() {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("products");
  const launch = useLaunchState();
  const locked = launch && launch.phase !== "launched";
  useEffect(() => { api.get(`/marketplace/ranchers/${id}`).then(r => setD(r.data)).catch(() => setD({ missing: true })); }, [id]);
  const follow = async () => {
    if (!user) { window.location.href = `/login?next=/ranchers/${id}`; return; }
    const { data } = await api.post(`/marketplace/ranchers/${id}/follow`);
    setD(x => ({ ...x, following: data.following, follower_count: data.follower_count }));
  };
  const cover = useMemo(() => d?.cover_image_url || d?.image_url, [d]);
  if (!d) return <div className="max-w-7xl mx-auto px-6 py-20">Loading…</div>;
  if (d.missing) return <div className="max-w-7xl mx-auto px-6 py-20 text-center"><h1 className="font-display text-4xl">Rancher not found</h1><Link to="/ranchers" className="btn-primary mt-6 inline-flex">Browse all ranchers</Link></div>;
  if (locked) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="rancher-storefront-locked">
      <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--ff-primary)] text-white"><Lock size={26}/></span>
      <p className="overline mt-6">Revealed at launch</p>
      <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight">Our founding ranchers unlock November 27.</h1>
      <p className="mt-4 text-[var(--ff-text-secondary)]">We're locking in our founding ranchers now. Names, photos, and storefronts go live on launch day — become a member to be first in line.</p>
      <div className="mt-8 flex gap-3 justify-center">
        <Link to="/pricing" className="btn-primary">Become a member</Link>
        <Link to="/ranchers" className="btn-secondary">Back to ranchers</Link>
      </div>
    </div>
  );
  const liveDrops = d.drops.filter(x => x.phase !== "ended");

  return (
    <div data-testid="rancher-storefront">
      <Seo title={`${d.farm_name} — Rancher storefront`} description={(d.story || "").slice(0, 160)} path={`/ranchers/${id}`}/>
      <div className="relative h-[46vh] min-h-[320px] bg-black overflow-hidden">
        {cover && <img src={cover} alt={d.farm_name} className="absolute inset-0 w-full h-full object-cover"/>}
        <div className="absolute inset-0 hero-overlay"/>
        <div className="absolute bottom-8 left-0 right-0 max-w-7xl mx-auto px-6 text-white flex flex-wrap items-end gap-5">
          {d.logo_url && <img src={d.logo_url} alt="" className="w-20 h-20 rounded-full border-2 border-white bg-white object-cover" data-testid="rancher-logo"/>}
          <div className="flex-1 min-w-[240px]">
            <p className="overline text-[var(--ff-accent-kraft)] flex items-center gap-1" data-testid="farm-location"><MapPin size={12}/> {d.location}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-1 leading-tight" data-testid="farm-name">{d.farm_name}</h1>
            <div className="mt-2 flex items-center gap-4 text-white/90"><Stars avg={d.rating.avg} count={d.rating.count}/><span className="text-xs">{d.follower_count} follower{d.follower_count === 1 ? "" : "s"}</span></div>
          </div>
          <button onClick={follow} className={d.following ? "btn-secondary" : "btn-citrus"} data-testid="rancher-follow-btn">
            <Heart size={16} className={d.following ? "fill-current" : ""}/> {d.following ? "Following" : "Follow this ranch"}
          </button>
        </div>
      </div>

      <div className="border-b border-[var(--ff-border)] bg-[var(--ff-cream)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap gap-x-8 gap-y-2 text-xs text-[var(--ff-text-secondary)]" data-testid="rancher-trust-strip">
          <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[var(--ff-primary)]"/> Farm To You approved rancher</span>
          <span className="flex items-center gap-1.5"><Truck size={14} className="text-[var(--ff-primary)]"/> Ships directly from {d.state || "the ranch"}</span>
          {d.ships_to_regions && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[var(--ff-primary)]"/> Ships to {d.ships_to_regions}</span>}
          <span className="ml-auto italic">Independently owned &amp; operated — sold and fulfilled by {d.farm_name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-12 gap-10">
        <aside className="md:col-span-4 space-y-8">
          <div><p className="overline">Our story</p><p className="mt-3 text-[var(--ff-text-secondary)] leading-relaxed" data-testid="farm-story">{d.story}</p><p className="mt-3 text-sm text-[var(--ff-text-muted)]">Owner: {d.owner_name}</p></div>
          {(d.practices || []).length > 0 && <div data-testid="rancher-practices"><p className="overline text-[var(--ff-primary)]">Ranching practices</p><div className="mt-3 flex flex-wrap gap-2">{d.practices.map(p => <span key={p} className="inline-flex items-center gap-1 bg-[var(--ff-accent-kraft)]/40 border border-[var(--ff-accent-kraft)] text-[var(--ff-primary)] rounded-sm px-3 py-1 text-xs font-semibold uppercase tracking-wide"><Wheat size={11}/> {p}</span>)}</div></div>}
          {(d.certifications || []).length > 0 && <div data-testid="rancher-certifications"><p className="overline text-[var(--ff-primary)]">Certifications &amp; standards</p><ul className="mt-3 space-y-1 text-sm">{d.certifications.map(c => <li key={c} className="flex items-center gap-2"><Award size={14} className="text-[var(--ff-primary)]"/> {c}</li>)}</ul></div>}
          {(d.specialties || []).length > 0 && <div data-testid="rancher-specialties"><p className="overline text-[var(--ff-primary)]">Specialties</p><p className="mt-2 text-sm">{d.specialties.join(" · ")}</p></div>}
          <div className="card-flat p-5" data-testid="rancher-shipping-info">
            <p className="overline flex items-center gap-1"><Truck size={12}/> Shipping from this ranch</p>
            <p className="mt-2 text-sm text-[var(--ff-text-secondary)]">{d.shipping_info || "This rancher packs and ships every order directly. Transit times vary by destination."}</p>
          </div>
        </aside>

        <div className="md:col-span-8">
          <div className="flex gap-2 border-b border-[var(--ff-border)]" data-testid="storefront-tabs">
            {[["products", `Products (${d.products.length})`], ["drops", `Drops (${liveDrops.length})`], ["reviews", `Reviews (${d.reviews.length})`]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`storefront-tab-${k}`} className={`px-4 py-3 text-sm border-b-2 -mb-px ${tab === k ? "border-[var(--ff-primary)] text-[var(--ff-primary)] font-medium" : "border-transparent text-[var(--ff-text-muted)]"}`}>{l}</button>
            ))}
          </div>
          {tab === "products" && (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="farm-products-grid">
              {d.products.map(p => <ProductCard key={p.id} p={p}/>)}
              {d.products.length === 0 && <p className="col-span-full text-[var(--ff-text-muted)] py-10 text-center">No products listed yet — follow this ranch to hear when they drop.</p>}
            </div>
          )}
          {tab === "drops" && (
            <div className="mt-6 space-y-3" data-testid="storefront-drops">
              {liveDrops.map(x => (
                <Link key={x.slug} to={`/drops/${x.slug}`} className="card-flat p-5 flex items-center gap-4 hover:border-[var(--ff-primary)]" data-testid={`storefront-drop-${x.slug}`}>
                  {x.image_url && <img src={x.image_url} alt="" className="w-20 h-20 rounded-lg object-cover"/>}
                  <div className="flex-1"><p className="overline">{x.phase === "upcoming" ? "Upcoming drop" : "Live now"}</p><p className="font-display text-xl">{x.title}</p><p className="text-xs text-[var(--ff-text-muted)]">Opens {new Date(x.drops_at).toLocaleString()}</p></div>
                  <ChevronRight size={18}/>
                </Link>
              ))}
              {liveDrops.length === 0 && <p className="text-[var(--ff-text-muted)] py-10 text-center">No upcoming drops from this ranch right now.</p>}
            </div>
          )}
          {tab === "reviews" && (
            <div className="mt-6 space-y-4" data-testid="storefront-reviews">
              {d.reviews.map(r => (
                <div key={r.id} className="card-flat p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between"><Stars avg={r.rating} count={1}/><span className="text-xs text-[var(--ff-text-muted)]">{(r.created_at || "").slice(0, 10)}</span></div>
                  {r.title && <p className="font-medium mt-2">{r.title}</p>}
                  <p className="text-sm text-[var(--ff-text-secondary)] mt-1">{r.body}</p>
                  <p className="text-xs mt-2 text-[var(--ff-text-muted)]">{r.author} · {r.verified_purchase && <span className="text-[var(--ff-primary)]">✓ Verified purchase</span>}{r.product_name ? ` · ${r.product_name}` : ""}</p>
                  {r.response && <div className="mt-3 bg-[var(--ff-muted)] rounded-lg p-3 text-sm"><p className="overline">Response from {r.response.by}</p><p className="mt-1">{r.response.body}</p></div>}
                </div>
              ))}
              {d.reviews.length === 0 && <p className="text-[var(--ff-text-muted)] py-10 text-center">No reviews yet. Verified customers can review after delivery.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
